import { useState } from "react";

import { usePostHog } from "posthog-js/react";
import { useSWRConfig } from "swr";

import { useAPI, useDisclosure } from "hooks";
import { isShipmentTemplatesListKey } from "utils/templates";

import { CreateShipmentTemplateButton } from "./CreateShipmentTemplateButton";
import { ClientAppEvent } from "../../../../constants";

import type { CreateShipmentBody } from "types/shipment";

interface Props {
  getShipmentBody: (isTemplate: boolean) => CreateShipmentBody;
  // A reason the form can't be saved as a template yet, or null.
  getTemplateError?: () => string | null;
}

interface CreateShipmentTemplateBody extends CreateShipmentBody {
  template_name?: string;
}

export function CreateShipmentTemplateButtonContainer(props: Props) {
  const { getShipmentBody, getTemplateError = () => null } = props;
  const { isOpen, onClose, onOpen } = useDisclosure(false);
  const [templateName, setTemplateName] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const { api } = useAPI();
  const { mutate } = useSWRConfig();
  const posthog = usePostHog();

  const getTemplateBody = (): CreateShipmentTemplateBody => {
    const body = getShipmentBody(true);
    /*
    Create shipment form has start/end route points pre-defined.
    if the point location is not defined, it means that no
    point was actually edited by the user (and therefore there is no need to save
    it along with the rest of the template)
    */
    if (
      body?.route?.length === 2 &&
      body.route[0]?.point_location === undefined &&
      body.route[1]?.point_location === undefined
    ) {
      body.route = [];
    }
    const templateBody: CreateShipmentTemplateBody = {
      ...body,
      template_name: templateName,
    };
    return templateBody;
  };

  const onSubmit = () => {
    const templateError = getTemplateError();
    if (templateError !== null) {
      setError(templateError);
      return;
    }
    const body = getTemplateBody();
    setSuccess(false);
    setIsLoading(true);
    api("post", "/shipment-templates", body)
      .then(() => {
        posthog?.capture(ClientAppEvent.SHIPMENT_TEMPLATE_CREATED_FROM_FORM, {
          template_name: templateName || undefined,
          has_route_points: body?.route && body.route.length > 0,
          equipment_types_count: body?.equipment_types?.length || 0,
          has_book_now: !!(
            body?.shipment?.book_now_price && body?.shipment?.book_now_currency
          ),
          shipment_type: body?.shipment?.type || undefined,
          is_direct_booking: body.direct_booking !== undefined,
        });
        setSuccess(true);
        setError(null);
        setTemplateName("");
        onClose();
        mutate(isShipmentTemplatesListKey);
      })
      .catch((e) => {
        setError(
          e?.response?.data?.message ??
            "Failed to create shipment template. Please contact us.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onModalClose = () => {
    if (success) {
      // if a template was posted, reset the form
      setTemplateName("");
    }
    setSuccess(false);
    setError(null);
    onClose();
  };

  return (
    <CreateShipmentTemplateButton
      loading={isLoading}
      modalOpen={isOpen}
      onModalClose={onModalClose}
      onModalOpen={onOpen}
      onSubmit={onSubmit}
      templateName={templateName}
      setTemplateName={(newValue: string) => {
        setTemplateName(newValue);
      }}
      error={error}
      success={success}
    />
  );
}
